import React, { useState, useEffect } from 'react';
import { loadClients, saveClients } from './storage';
import LoginScreen    from './views/LoginScreen';
import AgencyDashboard from './views/AgencyDashboard';
import AgencyClientView from './views/AgencyClientView';
import OnboardingHub  from './onboarding/OnboardingHub';
import Section        from './components/Section';
import { NavBar, Btn } from './components/UI';
import { card, mono } from './styles';
import { GREEN, BLACK } from './constants';

export default function App() {
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [role,     setRole]     = useState(null);   // 'agency' | 'client' | null
  const [authClient, setAuthClient] = useState(null); // client object for client login
  const [selected, setSelected] = useState(null);   // agency: selected client object
  const [showHandover, setShowHandover] = useState(false); // client: viewing handover guide

  // Load shared client state on mount.
  useEffect(() => {
    let active = true;
    (async () => {
      const loaded = await loadClients();
      if (!active) return;
      setClients(loaded);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Persist on every change
  useEffect(() => {
    if (loading) return;
    saveClients(clients);
  }, [clients, loading]);

  function handleLogin(r, cl) {
    setRole(r);
    if (r === 'client') {
      // Bind to the live client record so changes propagate
      setAuthClient(cl);
    }
  }

  function handleLogout() {
    setRole(null);
    setAuthClient(null);
    setSelected(null);
    setShowHandover(false);
  }

  function createClient(cl) {
    setClients(prev => [...prev, cl]);
  }

  function updateClient(updated) {
    setClients(prev => prev.map(c => c.id === updated.id ? updated : c));
    // Keep selected and authClient in sync
    if (selected && selected.id === updated.id) setSelected(updated);
    if (authClient && authClient.id === updated.id) setAuthClient(updated);
  }

  // ── Not logged in ──────────────────────────────────────────────
  if (!role) {
    return (
      <LoginScreen
        clients={clients}
        onLogin={handleLogin}
      />
    );
  }

  // ── Agency: single client view ────────────────────────────────
  if (role === 'agency' && selected) {
    return (
      <AgencyClientView
        client={selected}
        onBack={() => setSelected(null)}
        onUpdate={updateClient}
      />
    );
  }

  // ── Agency: dashboard ─────────────────────────────────────────
  if (role === 'agency') {
    return (
      <AgencyDashboard
        clients={clients}
        loading={loading}
        onSelect={setSelected}
        onCreate={createClient}
        onLogout={handleLogout}
      />
    );
  }

  // ── Client: handover guide ────────────────────────────────────
  if (role === 'client' && showHandover) {
    // Find the freshest version of the client record
    const live = clients.find(c => c.id === authClient.id) || authClient;
    return (
      <div style={{ minHeight: '100vh', background: '#f5f5f2', fontFamily: 'system-ui, sans-serif' }}>
        <NavBar
          title={live.name}
          sub="Handover Guide"
          onBack={() => setShowHandover(false)}
          right={<Btn v="outline" sm onClick={handleLogout}>Log out</Btn>}
        />
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '24px 16px' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 22, fontWeight: 800 }}>Your Handover Guide</div>
            <div style={mono}>Everything you need to know about your account</div>
          </div>
          {live.sections.map(sec => (
            <Section key={sec.id} sec={sec} editable={false} onUpdate={() => {}} />
          ))}
        </div>
      </div>
    );
  }

  // ── Client: onboarding hub ────────────────────────────────────
  if (role === 'client') {
    const live = clients.find(c => c.id === authClient.id) || authClient;
    return (
      <OnboardingHub
        client={live}
        onUpdate={updateClient}
        onHandover={() => setShowHandover(true)}
        onLogout={handleLogout}
      />
    );
  }

  return null;
}
